import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { Portfolio, RewardsProduct } from 'types/rewards'
import { RewardsSheet } from '../components/Sheet/RewardsSheet'
import { RewardsAction } from '../types'

interface RewardsSheetProviderProps {
  userPortfolios: Portfolio[]
  rewardsProducts: RewardsProduct[]
  selectedCurrency?: string
  children: ReactNode
}

interface RewardsSheetContextValue {
  isSheetOpen: boolean
  rewardsSheetAction: RewardsAction | null
  userPortfolios: Portfolio[]
  rewardsProducts: RewardsProduct[]
  selectedCurrency: string | null
  handleSheetOpen: (action: Omit<RewardsAction, 'onClose'>) => void
  handleSheetUpdate: (action: Omit<RewardsAction, 'onClose' | 'actionId'>) => void
}
const RewardsSheetContext = createContext<RewardsSheetContextValue | null>(null)

export const useRewardsSheetContext = () => {
  const context = useContext(RewardsSheetContext)

  if (!context) {
    throw new Error('useRewardsActionContext must be used within a RewardsActionProvider')
  }
  return context
}

export const RewardsSheetProvider = ({
  userPortfolios,
  rewardsProducts,
  selectedCurrency,
  children,
}: RewardsSheetProviderProps) => {
  const [rewardsSheetAction, setRewardsSheetAction] = useState<RewardsAction | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const handleSheetOpen = (action: Omit<RewardsAction, 'onClose'>) => {
    setRewardsSheetAction({ ...action, onClose: () => setIsSheetOpen(false) })
    setIsSheetOpen(true)
  }

  const handleSheetUpdate = (action: Omit<RewardsAction, 'onClose' | 'actionId'>) => {
    setRewardsSheetAction({
      ...action,
      actionId: rewardsSheetAction?.actionId,
      onClose: () => setIsSheetOpen(false),
    })
  }

  const getSelectedCurrency = useCallback(() => {
    if (selectedCurrency) return selectedCurrency
    if (rewardsProducts.length === 1) return rewardsProducts[0]?.currency

    return selectedCurrency
  }, [selectedCurrency, rewardsProducts])

  const contextValue = useMemo<RewardsSheetContextValue>(
    () => ({
      isSheetOpen,
      rewardsSheetAction,
      userPortfolios,
      rewardsProducts,
      handleSheetOpen,
      handleSheetUpdate,
      selectedCurrency: getSelectedCurrency(),
    }),
    [isSheetOpen, rewardsSheetAction, handleSheetOpen]
  )

  return (
    <RewardsSheetContext.Provider value={contextValue}>
      {children}
      <RewardsSheet isSheetOpen={isSheetOpen} />
    </RewardsSheetContext.Provider>
  )
}
